import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import enTranslations from './locales/en.json';
import frTranslations from './locales/fr.json';

export const supportedLanguages = {
  en: { name: 'English', nativeName: 'English' },
  fr: { name: 'French', nativeName: 'Français' },
} as const;

export const supportedRegions = {
  US: { name: 'United States', currency: 'USD', locale: 'en-US' },
  CA: { name: 'Canada', currency: 'CAD', locale: 'en-CA' },
} as const;

export const domainConfig = {
  US: {
    domains: ['motzulabs.com', 'motzu-labs.web.app', 'localhost'],
    defaultLanguage: 'en' as const,
    defaultRegion: 'US' as const,
  },
  CA: {
    domains: ['motzulabs.ca'],
    defaultLanguage: 'en' as const,  // Could be 'fr' for Quebec users
    defaultRegion: 'CA' as const,
  },
} as const;

export type SupportedLanguage = keyof typeof supportedLanguages;
export type SupportedRegion = keyof typeof supportedRegions;
export type SupportedCurrency = 'USD' | 'CAD';

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: enTranslations },
      fr: { translation: frTranslations },
    },
    fallbackLng: 'en',
    supportedLngs: ['en', 'fr'],
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng',
    },
  });

// View-as picker copy (MOT-175). Merged into the loaded bundles, without
// overwriting anything already defined, so the 150KB+ locale JSON files stay
// untouched by this change.
i18n.addResourceBundle(
  'en',
  'translation',
  {
    shell: {
      viewAs: {
        current: 'Current',
        truncated: 'Showing {{shown}} of {{total}} accounts — keep typing to narrow the list',
      },
    },
  },
  true,
  false
);
i18n.addResourceBundle(
  'fr',
  'translation',
  {
    shell: {
      viewAs: {
        current: 'Actuel',
        truncated: 'Affichage de {{shown}} comptes sur {{total}} — continuez à saisir pour affiner la liste',
      },
    },
  },
  true,
  false
);

export default i18n;

export const formatCurrency = (
  amount: number,
  currency: SupportedCurrency = 'USD',
  locale?: string
): string => {
  const effectiveLocale = locale || (currency === 'CAD' ? 'en-CA' : 'en-US');
  return new Intl.NumberFormat(effectiveLocale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

export const formatDate = (
  date: Date | string | number,
  locale: string = 'en-US',
  options?: Intl.DateTimeFormatOptions
): string => {
  const dateObj = date instanceof Date ? date : new Date(date);
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options,
  }).format(dateObj);
};

export const formatNumber = (
  value: number,
  locale: string = 'en-US',
  options?: Intl.NumberFormatOptions
): string => {
  return new Intl.NumberFormat(locale, options).format(value);
};
